import ArticleIcon from '@mui/icons-material/Article';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LayersIcon from '@mui/icons-material/Layers';
import { Box, Button, Card, CardActionArea, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  ArticleAuthorMeta,
  formatRelativeTime,
  getArticlePath,
  getArticleText,
  getCollectionArticlePath,
  getCollectionPath,
  htmlToPreviewText,
} from './publicKB.helpers';

export default function PublicKBCollections({
  brandId,
  collections,
  collectionSlug,
  selectedCollectionInfo,
  visibleArticles,
  showArticleList,
  search,
  onClearSearch,
}) {
  if (!showArticleList) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 3,
          justifyContent: 'center',
        }}
      >
        {collections.map((collection) => (
          <Card key={collection.name} variant="outlined" sx={{ minHeight: 220, borderRadius: 1, borderColor: '#e5e7eb', boxShadow: 'none' }}>
            <CardActionArea component={RouterLink} to={getCollectionPath(brandId, collection.slug)} sx={{ height: '100%', p: 3 }}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <LayersIcon sx={{ color: 'secondary.main', fontSize: 48 }} />
                <Box>
                  <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 900 }}>
                    {collection.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.main', mt: 1 }}>
                    {collection.articles.length} {collection.articles.length === 1 ? 'article' : 'articles'}
                  </Typography>
                  {collection.latestUpdatedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Last Update {formatRelativeTime(collection.latestUpdatedAt)}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardActionArea>
          </Card>
        ))}
        {!collections.length && (
          <Typography variant="body2" color="text.secondary">
            No public knowledge base collections found.
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 14 }}>
        <Button component={RouterLink} to={`/knowledge-base/${brandId}`} sx={{ color: '#fff', p: 0, minWidth: 'auto', fontWeight: 800 }} onClick={onClearSearch}>
          All Collections
        </Button>
        {collectionSlug && (
          <>
            <ChevronRightIcon fontSize="small" />
            <Typography sx={{ color: '#fff', fontSize: 14, fontWeight: 800 }}>{selectedCollectionInfo?.name || collectionSlug}</Typography>
          </>
        )}
        {search.trim() && (
          <>
            <ChevronRightIcon fontSize="small" />
            <Typography sx={{ color: 'rgba(255,255,255,0.88)', fontSize: 14 }}>Search results</Typography>
          </>
        )}
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 5 }}>
        <Card variant="outlined" sx={{ borderRadius: 1, borderColor: '#e5e7eb', boxShadow: 'none', alignSelf: 'start' }}>
          <Stack spacing={2.5} alignItems="center" textAlign="center" sx={{ p: 3, borderBottom: '1px solid #edf0f5' }}>
            <LayersIcon sx={{ color: 'secondary.main', fontSize: 52 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                {selectedCollectionInfo?.name || 'All Collections'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {visibleArticles.length} {visibleArticles.length === 1 ? 'article' : 'articles'}
              </Typography>
            </Box>
          </Stack>
          <Stack spacing={1.5} sx={{ p: 3 }}>
            <Typography variant="caption" color="text.secondary">
              chat support
            </Typography>
            <ArticleAuthorMeta article={visibleArticles[0]} compact />
          </Stack>
        </Card>

        <Stack spacing={2}>
          {visibleArticles.map((article) => (
            <Card key={article.id} variant="outlined" sx={{ borderRadius: 1, borderColor: '#e5e7eb', boxShadow: 'none' }}>
              <CardActionArea
                component={RouterLink}
                to={collectionSlug ? getCollectionArticlePath(brandId, collectionSlug, article.slug) : getArticlePath(brandId, article)}
                sx={{ p: 3 }}
              >
                <Stack direction="row" spacing={3} alignItems="center">
                  <ArticleIcon sx={{ color: 'secondary.main', fontSize: 44 }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 900 }}>
                      {article.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last Update {formatRelativeTime(article.updated_at)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      {htmlToPreviewText(getArticleText(article)).slice(0, 180)}
                    </Typography>
                  </Box>
                  <ChevronRightIcon sx={{ color: 'primary.main' }} />
                </Stack>
              </CardActionArea>
            </Card>
          ))}
          {!visibleArticles.length && (
            <Typography variant="body2" color="text.secondary">
              No public knowledge base articles found.
            </Typography>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}
